import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/custom/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Trash2, Upload, Sparkles, Star, Wand2, DollarSign, CheckCircle2 } from 'lucide-react';
import api from '../../utils/api';
import BulkOperationStatus from '../../components/custom/BulkOperationStatus';

function AdminProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [fixingMetadata, setFixingMetadata] = useState(false);
  const [bulkFixingMetadata, setBulkFixingMetadata] = useState(false);
  const [suggestingPrice, setSuggestingPrice] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [aiAgeGroupLoading, setAiAgeGroupLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    age_groups: [],
    price: '',
    original_price: '',
    colors: '',
    materials: '',
    features: '',
    image_urls: [],
    stock_count: 100,
  });
  
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchAgeGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage]);

  const fetchProducts = async () => {
    try {
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products?limit=${itemsPerPage}&offset=${offset}`
      );
      const data = await response.json();
      setProducts(data.products || []);
      setTotalProducts(data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/categories`
      );
      const data = await response.json();
      // API returns array directly
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      // Set fallback categories if API fails
      setCategories([
        { name: 'Education', description: 'Educational items' },
        { name: 'Toys', description: 'Toys and games' },
        { name: 'Books', description: 'Books and reading materials' }
      ]);
    }
  };

  const fetchAgeGroups = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/age-groups/`
      );
      const data = await response.json();
      setAgeGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch age groups:', error);
      setAgeGroups([]);
    }
  };

  const handleEdit = async (product) => {
    // Fetch fresh product data from API to ensure we have latest updates
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${product.id}`);
      const freshProduct = await response.json();
      
      setFormData({
        id: freshProduct.id,
        title: freshProduct.title,
        description: freshProduct.description,
        category: freshProduct.category,
        category_name: freshProduct.category_name,
        age_groups: freshProduct.age_groups || [],
        price: freshProduct.price.toString(),
        original_price: freshProduct.original_price?.toString() || '',
        colors: freshProduct.colors?.join(', ') || '',
        materials: freshProduct.materials?.join(', ') || '',
        features: freshProduct.features?.join(', ') || '',
        image_urls: freshProduct.image_urls || [],
        stock_count: freshProduct.stock_count || 100,
      });
      setShowDialog(true);
    } catch (error) {
      console.error('Error fetching product:', error);
      // Fallback to cached data if API fails
      setFormData({
        id: product.id,
        title: product.title,
        description: product.description,
        category: product.category,
        category_name: product.category_name,
        age_groups: product.age_groups || [],
        price: product.price.toString(),
        original_price: product.original_price?.toString() || '',
        colors: product.colors?.join(', ') || '',
        materials: product.materials?.join(', ') || '',
        features: product.features?.join(', ') || '',
        image_urls: product.image_urls || [],
        stock_count: product.stock_count || 100,
      });
      setShowDialog(true);
    }
  };

  const handleAdd = () => {
    setFormData({
      title: '',
      description: '',
      category: categories.length > 0 ? categories[0].name.toLowerCase().replace(/\s+/g, '-') : '',
      age_groups: [],
      price: '',
      original_price: '',
      colors: '',
      materials: '',
      features: '',
      image_urls: [],
      stock_count: 100,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const productData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        age_groups: formData.age_groups,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        colors: formData.colors ? formData.colors.split(',').map(c => c.trim()) : [],
        materials: formData.materials ? formData.materials.split(',').map(m => m.trim()) : [],
        features: formData.features ? formData.features.split(',').map(f => f.trim()) : [],
        image_urls: formData.image_urls,
        stock_count: parseInt(formData.stock_count),
      };

      if (formData.id) {
        await api.adminUpdateProduct(formData.id, productData, token);
        toast.success('Product updated successfully!');
      } else {
        await api.adminCreateProduct(productData, token);
        toast.success('Product created successfully!');
      }

      setShowDialog(false);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await api.adminDeleteProduct(productId, token);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleImageUploadForAI = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setExtracting(true);
    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Image = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
          
          toast.info('🤖 AI is analyzing the image...');
          const result = await api.adminAIExtract(base64Image, token);
          
          if (result.success && result.data) {
            // Get the uploaded image URL from the response
            const uploadedImageUrl = result.uploaded_image_url;
            
            // Auto-fill form with AI extracted data AND the uploaded image
            setFormData({
              ...formData,
              title: result.data.title || formData.title,
              description: result.data.description || formData.description,
              category: result.data.category || formData.category,
              age_range: result.data.age_range || formData.age_range,
              price: result.data.price_suggestion?.toString() || formData.price,
              colors: result.data.colors?.join(', ') || formData.colors,
              materials: result.data.materials?.join(', ') || formData.materials,
              features: result.data.features?.join(', ') || formData.features,
              // Add the uploaded image to image_urls array
              image_urls: uploadedImageUrl ? [uploadedImageUrl, ...formData.image_urls] : formData.image_urls,
            });
            
            if (uploadedImageUrl) {
              toast.success('✨ AI extraction successful! Image uploaded and data extracted.');
            } else {
              toast.success('✨ AI extraction successful! Review and adjust the data. Note: Image upload failed, please upload manually.');
            }
          } else {
            toast.error(result.detail || 'Failed to extract product info');
          }
        } catch (error) {
          console.error('AI extraction error:', error);
          toast.error('AI extraction failed. Please try again.');
        } finally {
          setExtracting(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to process image');
      setExtracting(false);
    }
  };

  const handleEnrichProduct = async (productId) => {
    setEnriching(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/enrich-product`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({ product_id: productId })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('✨ Product enriched with AI!');
        fetchProducts(); // Refresh products list
      } else {
        toast.error(data.detail || 'Failed to enrich product');
      }
    } catch (error) {
      console.error('Error enriching product:', error);
      toast.error('Failed to enrich product');
    } finally {
      setEnriching(false);
    }
  };

  const handleGenerateReviews = async (productId) => {
    setEnriching(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/generate-reviews/${productId}?count=3`, {
        method: 'POST',
        headers: {
          'Authorization': token
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`✨ Generated ${data.count} reviews for product!`);
        fetchProducts();
      } else {
        toast.error('Failed to generate reviews');
      }
    } catch (error) {
      console.error('Error generating reviews:', error);
      toast.error('Failed to generate reviews');
    } finally {
      setEnriching(false);
    }
  };

  const handleBulkGenerateReviews = async () => {
    if (!window.confirm('Generate reviews for all products without reviews? The process will run in the background.')) {
      return;
    }

    setEnriching(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/generate-reviews-bulk?page=${currentPage}&limit=10`,
        {
          method: 'POST',
          headers: {
            'Authorization': token
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        toast.success(`✨ ${data.message}. Track progress in the panel below.`, { duration: 5000 });
      } else {
        toast.error('Failed to start bulk review generation');
      }
    } catch (error) {
      console.error('Error bulk generating reviews:', error);
      toast.error('Failed to start bulk review generation');
    } finally {
      setEnriching(false);
    }
  };

  const handleBulkEnrich = async () => {
    if (!confirm('This will enrich ALL products with missing data using AI. The process will run in the background. Continue?')) {
      return;
    }

    setBulkEnriching(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/enrich-all-products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`✨ ${data.message}. Track progress in the panel below.`, { duration: 5000 });
      } else {
        toast.error('Failed to start bulk enrichment');
      }
    } catch (error) {
      console.error('Error bulk enriching:', error);
      toast.error('Failed to start bulk enrichment');
    } finally {
      setBulkEnriching(false);
    }
  };

  const handleBulkFixMetadata = async () => {
    if (!confirm('This will auto-align age groups and categories for ALL products using AI. The process will run in the background. Continue?')) {
      return;
    }

    setBulkFixingMetadata(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/fix-metadata-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`✨ ${data.message}. Track progress in the panel below.`, { duration: 5000 });
      } else {
        toast.error('Failed to start bulk metadata fix');
      }
    } catch (error) {
      console.error('Error bulk fixing metadata:', error);
      toast.error('Failed to start bulk metadata fix');
    } finally {
      setBulkFixingMetadata(false);
    }
  };

  // AI Age Group Analysis
  const handleAIAgeGroupAnalysis = async () => {
    setAiAgeGroupLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/ai-age-groups`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            product_ids: [], // Empty means analyze products
            analyze_images: true,
            check_all: true // NEW: Check ALL products including ones with age groups
          })
        }
      );

      const data = await response.json();
      
      if (data.suggestions && data.suggestions.length > 0) {
        setAiSuggestions(data.suggestions);
        
        const stats = data.stats || {};
        const mismatches = stats.mismatches || 0;
        const missing = stats.missing || 0;
        const correct = stats.correct || 0;
        
        toast.success(`AI analyzed ${data.suggestions.length} products!`, {
          description: `${mismatches} corrections needed, ${missing} missing age groups, ${correct} correct`
        });
      } else {
        toast.info('All products analyzed - no issues found!');
      }
    } catch (error) {
      console.error('Failed to analyze age groups:', error);
      toast.error('Failed to analyze products');
    } finally {
      setAiAgeGroupLoading(false);
    }
  };

  const handleApplyAgeGroupSuggestion = async (suggestion) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/apply-ai-age-groups`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: [{
              product_id: suggestion.product_id,
              age_groups: suggestion.suggested_age_groups
            }]
          })
        }
      );

      if (response.ok) {
        toast.success(`Age groups updated for ${suggestion.product_title}`);
        // Remove from suggestions
        setAiSuggestions(prev => prev.filter(s => s.product_id !== suggestion.product_id));
        // Refresh products
        fetchProducts();
      }
    } catch (error) {
      console.error('Failed to apply age group:', error);
      toast.error('Failed to update age groups');
    }
  };

  const handleApplyAllAgeGroups = async () => {
    if (aiSuggestions.length === 0) return;
    
    try {
      const updates = aiSuggestions.map(s => ({
        product_id: s.product_id,
        age_groups: s.suggested_age_groups
      }));

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/apply-ai-age-groups`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Updated ${data.updated_count} products with AI age groups!`);
        setAiSuggestions([]);
        fetchProducts();
      }
    } catch (error) {
      console.error('Failed to apply all age groups:', error);
      toast.error('Failed to bulk update');
    }
  };

  const handleFixStock = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/products/fix-stock`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      toast.success(data.message);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to fix stock');
    }
  };

  const handleFixMetadata = async (productId) => {
    setFixingMetadata(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/fix-product-metadata/${productId}`, {
        method: 'POST',
        headers: {
          'Authorization': token
        }
      });

      const data = await response.json();

      if (data.success) {
        if (data.applied) {
          toast.success(`✨ Metadata auto-aligned! Age: ${data.suggestions.age_group}, Category: ${data.suggestions.category}`);
          
          // If editing dialog is open, update the form data
          if (showDialog && formData.id === productId) {
            // Fetch the updated product to get the new values
            const productResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${productId}`);
            const updatedProduct = await productResponse.json();
            
            // Update form data with new category and age
            setFormData({
              ...formData,
              category: updatedProduct.category,
              category_name: updatedProduct.category_name,
              age_range: updatedProduct.age_group || formData.age_range
            });
          }
        } else {
          toast.info(`💡 Suggestion (${data.suggestions.confidence} confidence): Age: ${data.suggestions.age_group}, Category: ${data.suggestions.category}`);
        }
        fetchProducts();
      } else {
        toast.error('Failed to fix metadata');
      }
    } catch (error) {
      console.error('Error fixing metadata:', error);
      toast.error('Failed to fix metadata');
    } finally {
      setFixingMetadata(false);
    }
  };

  const handleSuggestPrice = async (productId) => {
    setSuggestingPrice(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/recommend-price/${productId}`, {
        method: 'POST',
        headers: {
          'Authorization': token
        }
      });

      const data = await response.json();

      if (data.success) {
        const rec = data.recommendations;
        toast.success(
          `💰 AI Price Suggestion: ₹${rec.recommended_price} (Range: ₹${rec.min_price}-₹${rec.max_price}). ${rec.reasoning}`,
          { duration: 8000 }
        );
      } else {
        toast.error('Failed to get price suggestion');
      }
    } catch (error) {
      console.error('Error suggesting price:', error);
      toast.error('Failed to get price suggestion');
    } finally {
      setSuggestingPrice(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Products</h1>
            <p className="text-muted-foreground">Manage your product catalog</p>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <Button
              onClick={handleBulkEnrich}
              disabled={bulkEnriching}
              variant="outline"
              className="gap-2 flex-shrink-0"
              data-testid="bulk-enrich-all-button"
            >
              {bulkEnriching ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="hidden sm:inline">Starting...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span className="hidden sm:inline">AI Enrich All</span>
                  <span className="sm:hidden">Enrich</span>
                </>
              )}
            </Button>
            <Button
              onClick={handleBulkGenerateReviews}
              disabled={enriching}
              variant="outline"
              className="gap-2 text-purple-600 border-purple-300 hover:bg-purple-50 flex-shrink-0"
              data-testid="bulk-generate-reviews-button"
            >
              {enriching ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="hidden sm:inline">Starting...</span>
                </>
              ) : (
                <>
                  <Star className="w-4 h-4" />
                  <span className="hidden sm:inline">Generate Reviews</span>
                  <span className="sm:hidden">Reviews</span>
                </>
              )}
            </Button>
            <Button
              onClick={handleBulkFixMetadata}
              disabled={bulkFixingMetadata}
              variant="outline"
              className="gap-2 text-blue-600 border-blue-300 hover:bg-blue-50 flex-shrink-0"
              data-testid="bulk-fix-metadata-button"
            >
              {bulkFixingMetadata ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="hidden sm:inline">Starting...</span>
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Fix All Metadata</span>
                  <span className="sm:hidden">Fix All</span>
                </>
              )}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleAIAgeGroupAnalysis}
              disabled={aiAgeGroupLoading}
              className="gap-2 text-purple-600 border-purple-300 hover:bg-purple-50 flex-shrink-0"
              title="AI analyze images to suggest age groups"
            >
              {aiAgeGroupLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="hidden sm:inline">Analyzing...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span className="hidden sm:inline">AI Age Groups</span>
                  <span className="sm:hidden">Age AI</span>
                </>
              )}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleFixStock} 
              className="flex-shrink-0"
              title="Fix products showing out of stock incorrectly"
            >
              <span className="hidden sm:inline">Fix Stock Status</span>
              <span className="sm:hidden">Stock</span>
            </Button>
            <Button 
              onClick={handleAdd} 
              className="flex-shrink-0"
              data-testid="add-product-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Add Product</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>

        {/* AI Age Group Suggestions Panel */}
        {aiSuggestions.length > 0 && (
          <Card className="mt-4 border-purple-200 bg-purple-50">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-purple-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  AI Age Group Suggestions ({aiSuggestions.length})
                </h3>
                <Button 
                  size="sm"
                  onClick={handleApplyAllAgeGroups}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Apply All
                </Button>
              </div>
              
              <div className="grid gap-3 max-h-96 overflow-y-auto">
                {aiSuggestions.map((suggestion, idx) => (
                  <Card key={idx} className={`p-3 ${
                    suggestion.correction_type === 'mismatch' ? 'bg-orange-50 border-orange-200' :
                    suggestion.correction_type === 'missing' ? 'bg-yellow-50 border-yellow-200' :
                    'bg-white'
                  }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-sm">{suggestion.product_title}</h4>
                          {suggestion.correction_type === 'mismatch' && (
                            <span className="px-2 py-0.5 bg-orange-600 text-white text-xs rounded">
                              CORRECTION NEEDED
                            </span>
                          )}
                          {suggestion.correction_type === 'missing' && (
                            <span className="px-2 py-0.5 bg-yellow-600 text-white text-xs rounded">
                              MISSING
                            </span>
                          )}
                          {suggestion.correction_type === 'correct' && (
                            <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded">
                              ✓ CORRECT
                            </span>
                          )}
                        </div>
                        
                        {/* Current vs Suggested Comparison */}
                        {suggestion.has_age_groups && (
                          <div className="mb-2 p-2 bg-gray-100 rounded text-xs">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-gray-600">Current:</span>
                              <div className="flex gap-1">
                                {suggestion.current_age_groups.map(ag => (
                                  <span key={ag} className={`px-2 py-0.5 rounded ${
                                    suggestion.is_mismatch ? 'bg-red-100 text-red-700 line-through' : 'bg-gray-200 text-gray-700'
                                  }`}>
                                    {ag} yrs
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex flex-wrap gap-2 mb-2">
                          <span className="text-xs font-semibold text-purple-700">AI Suggests:</span>
                          {suggestion.suggested_age_groups.map(ag => (
                            <span key={ag} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded font-medium">
                              {ag} years
                            </span>
                          ))}
                          <span className={`px-2 py-1 text-xs rounded ${
                            suggestion.confidence === 'high' ? 'bg-green-100 text-green-700' :
                            suggestion.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {suggestion.confidence} confidence
                          </span>
                          {suggestion.analyzed_with_image && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                              📸 Image analyzed
                            </span>
                          )}
                        </div>
                        
                        <p className="text-xs text-gray-600 mb-1">
                          <strong>Reason:</strong> {suggestion.reasoning}
                        </p>
                        
                        {suggestion.safety_notes && (
                          <p className="text-xs text-orange-600">
                            <strong>Safety:</strong> {suggestion.safety_notes}
                          </p>
                        )}
                        
                        <p className="text-xs text-purple-600 font-medium mt-2">
                          {suggestion.correction_note}
                        </p>
                      </div>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleApplyAgeGroupSuggestion(suggestion)}
                        className={`shrink-0 ${
                          suggestion.correction_type === 'correct' ? 'opacity-50' : ''
                        }`}
                        disabled={suggestion.correction_type === 'correct'}
                      >
                        {suggestion.correction_type === 'correct' ? '✓ OK' : 'Apply'}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => (
              <Card key={product.id} className="overflow-hidden">
                <img
                  src={product.image_urls[0] || 'https://via.placeholder.com/300'}
                  alt={product.title}
                  className="w-full h-40 sm:h-48 object-cover"
                />
                <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                  <h3 className="font-semibold line-clamp-2 text-sm sm:text-base">{product.title}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-lg sm:text-xl font-bold">₹{product.price}</span>
                    <span className={`text-xs px-2 py-1 rounded ${product.in_stock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {product.in_stock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>
                  {/* Action buttons - responsive 3x2 grid */}
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => handleEnrichProduct(product.id)}
                      disabled={enriching}
                      title="AI Fill product details"
                      data-testid="admin-product-ai-enrich-button"
                    >
                      {enriching ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Sparkles className="w-3 h-3" />
                      )}
                      <span className="ml-1">AI</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs text-purple-600 border-purple-300"
                      onClick={() => handleGenerateReviews(product.id)}
                      disabled={enriching}
                      title="Generate reviews"
                      data-testid="admin-product-generate-reviews-button"
                    >
                      <Star className="w-3 h-3" />
                      <span className="ml-1">Rev</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs text-blue-600 border-blue-300"
                      onClick={() => handleFixMetadata(product.id)}
                      disabled={fixingMetadata}
                      title="Auto-align age & category"
                      data-testid="admin-product-fix-metadata-button"
                    >
                      {fixingMetadata ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Wand2 className="w-3 h-3" />
                      )}
                      <span className="ml-1">Fix</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs text-green-600 border-green-300"
                      onClick={() => handleSuggestPrice(product.id)}
                      disabled={suggestingPrice}
                      title="AI price suggestion"
                      data-testid="admin-product-suggest-price-button"
                    >
                      {suggestingPrice ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <DollarSign className="w-3 h-3" />
                      )}
                      <span className="ml-1">Price</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => handleEdit(product)}
                      data-testid="admin-product-edit-button"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="text-xs"
                      onClick={() => handleDelete(product.id)}
                      data-testid="admin-product-delete-button"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Del
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalProducts > 0 && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t pt-4">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalProducts)} of {totalProducts} products
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              
              <div className="flex items-center gap-1">
                {[...Array(Math.ceil(totalProducts / itemsPerPage))].map((_, idx) => {
                  const pageNum = idx + 1;
                  const totalPages = Math.ceil(totalProducts / itemsPerPage);
                  
                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="w-9"
                      >
                        {pageNum}
                      </Button>
                    );
                  } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} className="px-1">...</span>;
                  }
                  return null;
                })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalProducts / itemsPerPage), prev + 1))}
                disabled={currentPage >= Math.ceil(totalProducts / itemsPerPage)}
              >
                Next
              </Button>
            </div>
            
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-2 border rounded-md text-sm"
            >
              <option value={12}>12 per page</option>
              <option value={24}>24 per page</option>
              <option value={48}>48 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        )}

        {/* Edit/Add Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{formData.id ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* AI Product Extraction */}
              <Card className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 border-purple-200 dark:border-purple-800">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-600 rounded-lg">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-1">
                      AI Product Extraction
                    </h3>
                    <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
                      Upload a product image and let AI automatically extract product details
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUploadForAI}
                        disabled={extracting}
                        className="hidden"
                        id="ai-image-upload"
                      />
                      <label htmlFor="ai-image-upload">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={extracting}
                          className="cursor-pointer bg-white dark:bg-gray-800"
                          asChild
                        >
                          <span>
                            {extracting ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Analyzing...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Upload & Extract
                              </>
                            )}
                          </span>
                        </Button>
                      </label>
                      {extracting && (
                        <span className="text-sm text-purple-600 dark:text-purple-400 animate-pulse">
                          AI is reading the image...
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>

              <div>
                <Label htmlFor="title">Product Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Rainbow Unicorn Lunchbox"
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Leak-proof, colorful lunchbox..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <select
                    id="category"
                    className="w-full h-10 rounded-md border border-input bg-background px-3"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option 
                        key={cat.name} 
                        value={cat.name.toLowerCase().replace(/\s+/g, '-')}
                      >
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {categories.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">Loading categories...</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="age_groups">Age Groups *</Label>
                  <div className="space-y-2">
                    {ageGroups.length > 0 ? (
                      ageGroups.map((ageGroup) => (
                        <label key={ageGroup.slug} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.age_groups.includes(ageGroup.slug)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  age_groups: [...formData.age_groups, ageGroup.slug]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  age_groups: formData.age_groups.filter(ag => ag !== ageGroup.slug)
                                });
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">{ageGroup.name}</span>
                        </label>
                      ))
                    ) : (
                      <p className="text-xs text-muted-foreground">Loading age groups...</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Select all age groups this product is suitable for
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="price">Price (₹) *</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="499"
                  />
                </div>
                <div>
                  <Label htmlFor="original_price">Original Price (₹)</Label>
                  <Input
                    id="original_price"
                    type="number"
                    value={formData.original_price}
                    onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                    placeholder="699"
                  />
                </div>
                <div>
                  <Label htmlFor="stock_count">Stock Count</Label>
                  <Input
                    id="stock_count"
                    type="number"
                    value={formData.stock_count}
                    onChange={(e) => {
                      const count = parseInt(e.target.value) || 0;
                      setFormData({ 
                        ...formData, 
                        stock_count: count,
                        in_stock: count > 0
                      });
                    }}
                    placeholder="100"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="in_stock"
                    checked={formData.in_stock}
                    onChange={(e) => setFormData({ ...formData, in_stock: e.target.checked })}
                    className="rounded border-gray-300 w-4 h-4"
                  />
                  <Label htmlFor="in_stock" className="cursor-pointer">
                    {formData.in_stock ? '✅ In Stock' : '❌ Out of Stock'}
                  </Label>
                </div>
              </div>

              <div>
                <Label htmlFor="colors">Colors (comma-separated)</Label>
                <Input
                  id="colors"
                  value={formData.colors}
                  onChange={(e) => setFormData({ ...formData, colors: e.target.value })}
                  placeholder="Pink, Purple, White"
                />
              </div>

              <div>
                <Label htmlFor="materials">Materials (comma-separated)</Label>
                <Input
                  id="materials"
                  value={formData.materials}
                  onChange={(e) => setFormData({ ...formData, materials: e.target.value })}
                  placeholder="BPA-free plastic, Stainless steel"
                />
              </div>

              <div>
                <Label htmlFor="features">Features (comma-separated)</Label>
                <Input
                  id="features"
                  value={formData.features}
                  onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                  placeholder="Leak-proof seal, Easy to clean"
                />
              </div>

              {/* Image URLs Section */}
              <div>
                <Label>Product Images</Label>
                <div className="mt-2 space-y-3">
                  {/* Image Preview */}
                  {formData.image_urls.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.image_urls.map((url, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={url}
                            alt={`Product ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newUrls = formData.image_urls.filter((_, i) => i !== index);
                              setFormData({ ...formData, image_urls: newUrls });
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add Image URL Input */}
                  <div className="flex gap-2">
                    <Input
                      id="new_image_url"
                      placeholder="Enter image URL or use AI extraction above"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          e.preventDefault();
                          setFormData({
                            ...formData,
                            image_urls: [...formData.image_urls, e.target.value.trim()]
                          });
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const input = document.getElementById('new_image_url');
                        if (input.value.trim()) {
                          setFormData({
                            ...formData,
                            image_urls: [...formData.image_urls, input.value.trim()]
                          });
                          input.value = '';
                        }
                      }}
                    >
                      Add
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tip: Use AI extraction above to auto-upload and add images, or paste Cloudflare image URLs here
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="flex-1">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    formData.id ? 'Update Product' : 'Create Product'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Bulk Operation Status Panel */}
      <BulkOperationStatus token={token} />
    </AdminLayout>
  );
}

export default AdminProductsPage;
